import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

type StackGap = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type StackAlign = 'start' | 'center' | 'end' | 'stretch'

interface StackProps {
    children: ReactNode
    gap?: StackGap
    align?: StackAlign
    /** Adds a top border with inner padding. Use for visually dividing a Stack from the content above it. */
    topBorder?: boolean
    /** Adds a bottom border with inner padding. Mirrors `topBorder` — e.g. editor toolbar shells. */
    bottomBorder?: boolean
    /** Adds a left border + inner left padding for indented call-out / note blocks. */
    leftBorder?: boolean
    /**
     * Extend the stack past its parent's horizontal padding (default: `-mx-6`, matching
     * a shadcn `CardContent`). Pair with children that supply their own horizontal padding.
     */
    bleed?: boolean
    /** Render horizontal dividers between children (`divide-y`). Cancels `gap`. */
    divideY?: boolean
    className?: string
    onClick?: (e: React.MouseEvent) => void
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const gapClasses: Record<StackGap, string> = {
    xs: 'space-y-1',
    sm: 'space-y-2',
    md: 'space-y-4',
    lg: 'space-y-6',
    xl: 'space-y-8',
}
const flexGapClasses: Record<StackGap, string> = {
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
    xl: 'gap-8',
}
const alignClasses: Record<StackAlign, string> = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
}
/* eslint-enable i18next/no-literal-string */

export function Stack({children, gap = 'md', align, topBorder, bottomBorder, leftBorder, bleed, divideY, className, onClick, 'data-testid': testId}: StackProps) {
    const isFlex = align !== undefined
    return (
        <div
            className={cn(
                isFlex ? 'flex flex-col' : undefined,
                divideY ? 'divide-y' : isFlex ? flexGapClasses[gap] : gapClasses[gap],
                isFlex && align ? alignClasses[align] : undefined,
                topBorder && 'border-t pt-3 mt-3',
                bottomBorder && 'border-b pb-3',
                leftBorder && 'border-l-2 pl-2 ml-1',
                bleed && '-mx-6',
                className,
            )}
            onClick={onClick}
            data-testid={testId}
        >
            {children}
        </div>
    )
}
